import React from "react";

import { useCurrentBreakpoint } from "../utils/hooks";

// True inside a RichNavigation subtree that a host has forced into mobile
// layout. Module-private: hosts use `RichNavMobileLayoutProvider`, descendants
// read the resolved viewport via `useRichNavViewport`.
const RichNavForceMobileLayoutContext = React.createContext(false);

// Wrap a RichNavigation subtree that must always render in mobile layout,
// independent of the shared `$mobile` breakpoint. Needed when a host shows the
// mobile nav panel at a boundary that differs from `$mobile` (e.g. the
// Tempur-Pedic header shows it at an inclusive `max-width: 768px`, one pixel
// wider than the shared `belowMobile`). Its mere presence forces mobile -- the
// panel only ever renders below the header's mobile boundary, so there is no
// desktop case to parameterize. Omitting it (the default) leaves the subtree on
// the shared breakpoint, so every other site renders exactly as before.
export const RichNavMobileLayoutProvider = ({
    children,
}: React.PropsWithChildren): React.JSX.Element => (
    <RichNavForceMobileLayoutContext.Provider value={true}>
        {children}
    </RichNavForceMobileLayoutContext.Provider>
);

// Drop-in replacement for `useCurrentBreakpoint()` inside the RichNavigation
// subtree. When a host has forced mobile layout, report the mobile side of the
// `$mobile` split -- `belowMobile` and its exact complement `aboveMobile` are
// set in lockstep so the viewport can't describe a contradictory state. Every
// other breakpoint (e.g. `aboveSmall`) comes from the shared hook unchanged.
export const useRichNavViewport = (): ReturnType<
    typeof useCurrentBreakpoint
> => {
    const shared = useCurrentBreakpoint();
    const forceMobileLayout = React.useContext(RichNavForceMobileLayoutContext);
    return forceMobileLayout
        ? { ...shared, belowMobile: true, aboveMobile: false }
        : shared;
};
